/* BI-08 · ÉQUIPE & RÔLES — and BI-15, its empty state.

   D-46 / 0018 (batch 13). Four roles, and every attribution is a role plus the
   establishments it reaches. The rule, as the schema enforces it:
     · owner and admin are unscoped — a `locations[]` on them is ignored, never
       a restriction (promoting a manager to admin must widen her access, never
       leave a stale list narrowing it);
     · a manager is scoped to her `locations[]`, and an empty list is nothing,
       not everything;
     · staff is the till, scoped the same way — the PIN is a person, not a
       permission.
   The seven-role matrix of WF-13 v5 / Bible §11 is deliberately not built; the
   « Portée » column it carried (a mix of establishments and « lecture ») is
   retired for the establishment column the four roles actually have.

   In day-one the workspace has one member and BI-15 is what shows (ruling
   30/08). D-31: the list is read through the establishment context — from
   « Tous les établissements » nothing changes. */
import { useNavigate } from 'react-router-dom'
import { Button, EmptyState, PageHeader } from '@/ui'
import { useLocations } from '@/lib/api'
import { ALL, scopedPath, useScope } from '@/lib/scope'
import { actsIn, ROLE_LABEL, roleRule, unscoped, type TeamMember } from './api'
import { useBill } from './ParamShell'

export default function BI08Equipe() {
  const b = useBill()
  const nav = useNavigate()
  const [scope] = useScope()
  const { data: places } = useLocations()
  const placeName = (id: string) => places?.find(l => l.id === id)?.name ?? id

  /** the establishment cell: « tout » for the unscoped roles, her names for the
   *  scoped ones — and « aucun » for the empty list, which is nothing, not
   *  everything (0018). */
  const where = (m: TeamMember) =>
    unscoped(m.role) ? 'tout' : m.locations.map(placeName).join(' · ') || 'aucun'

  if (b.teamEmpty) return (
    <section data-frame="BI-15" style={{ display: 'grid', gap: 14 }}>
      <PageHeader title="Équipe"
        aside={<span className="t-muted" style={{ fontSize: 12 }}>vous êtes seul sur cet espace</span>} />
      <EmptyState title="Qui travaille avec vous."
        why="C’est vide parce que vous êtes le seul membre pour l’instant. Vous pouvez lancer une campagne sans inviter personne : vous validerez les cadeaux vous-même."
        action={<>
          <Button variant="primary" size="sm"
            onClick={() => nav(scopedPath('/parametres/equipe/inviter', scope))}>Inviter un collaborateur</Button>
          <Button variant="quiet" size="sm">Voir les rôles</Button>
        </>}>
        <p>
          Vos collaborateurs n’ont pas besoin d’un compte complet pour remettre un cadeau : un code
          personnel sur la tablette du comptoir suffit, et chaque validation est tracée à leur nom.
          Les responsables, eux, peuvent voir les résultats et corriger une erreur.
        </p>
        <p>
          Inviter quelqu’un ne coûte rien et ne change pas votre offre — le nombre de personnes
          n’est pas facturé.
        </p>
      </EmptyState>
    </section>
  )

  /* D-31 · the context filters the list, it never forks it: in an establishment
     you see who acts there — the unscoped roles always, the scoped ones only if
     the establishment is on their list. */
  const team = b.team.filter(m => actsIn(m.role, m.locations, scope, ALL))

  return (
    <section data-frame="BI-08" style={{ display: 'grid', gap: 14 }}>
      <PageHeader title="Équipe &amp; rôles"
        actions={<Button variant="primary" size="sm"
          onClick={() => nav(scopedPath('/parametres/equipe/inviter', scope))}>Inviter</Button>} />
      <div className="tablewrap">
        <table className="tbl">
          <thead><tr><th>Membre</th><th>Rôle</th><th>Établissements</th><th>Valider</th><th>Facturation</th></tr></thead>
          <tbody>
            {team.map(m => (
              <tr key={m.id}>
                <td><strong>{m.name}</strong></td>
                <td>{ROLE_LABEL[m.role]}</td><td>{where(m)}</td><td>{m.validate}</td><td>{m.billing}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {roleRule((places ?? []).filter(l => l.status !== 'inactive').length).map(line => <p key={line} className="t-body">{line}</p>)}
    </section>
  )
}
